import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Heart, ArrowUp } from 'lucide-react';
import { type DashboardData } from '@/services/dashboardService';
import { formatDateRange } from '@/utils/dateFormatter';
import { imageSrc } from '@/utils/event';
import type { Event } from '@/types/event';
import EventWrapper from './EventWrapper';

interface OnlineCoachingProps {
  userData: DashboardData;
  events: Event[];
  loading?: boolean;
  error?: string | null;  
}

const OnlineCoaching: React.FC<OnlineCoachingProps> = ({ 
  userData, 
  events, 
  loading = true, 
  error = null 
}) => {
  const EventCard = ({ event }: { event: Event }) => {
    const sessionId = event?.next_schedule?.id;
    const finalImageSrc = imageSrc(event, 200, 600);
    const start = event?.next_schedule_date || event?.next_schedule?.schedule_at || event?.event_date as string;
    const end = event?.next_schedule?.end_at || event?.event_end_date as string;
    
    const dateInfo = formatDateRange(start, end);

    return (
      <div className="bg-white rounded-lg overflow-hidden shadow-lg">
        {/* Event Image */}
        <div className="relative h-52 overflow-hidden">              
          <img
            className="w-full h-full object-cover" 
            src={finalImageSrc}
            alt={event.title}
          />  
          {/* Close Button */}
          <Link to={`/online-coaching/register/${sessionId}`} className="absolute top-3 right-3 bg-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer">
            <ArrowUp size={24} className="rotate-45"/>
          </Link>
        </div>

        {/* Event Details */}
        <div className="p-6">
          <div className="flex gap-4">
            {/* Date */}
            <div className="flex-shrink-0">
              <div className="inline-flex flex-col justify-start items-center gap-0">
                <>
                  <div className="uppercase text-center text-indigo-700 text-xl font-semibold">
                    {dateInfo.startMonth}
                    {dateInfo.splitYear && (
                      <sup className="text-center text-gray-600 text-xs font-normal">
                        {dateInfo.startYear}
                      </sup>
                    )}
                  </div>
                  <div className="inline-flex justify-center items-center gap-1">
                    <span className="text-center text-gray-800 text-2xl font-bold">
                      {dateInfo.startDay}
                      {dateInfo.sameMonth && dateInfo.splitDay && `-${dateInfo.endDay}`}
                    </span>
                  </div>
                </>
                {(!dateInfo.sameMonth || dateInfo.splitMonth) && (
                  <>
                    <span className="text-center text-gray-600 text-xs font-normal">to</span>
                    <span className="uppercase text-center text-indigo-700 text-xl font-semibold">
                      {dateInfo.endMonth}
                      {dateInfo.splitYear && (
                        <sup className="text-center text-gray-600 text-xs font-normal">
                          {dateInfo.endYear}
                        </sup>
                      )}
                    </span>
                    <div className="inline-flex justify-center items-center gap-1">
                      <span className="text-center text-gray-800 text-2xl font-bold">
                        {dateInfo.endDay}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Event Info */}
            <div className="flex-1">
              <h3 className="line-clamp-1 text-gray-800 text-xl font-semibold mb-2">
                {event.title}
              </h3>
              <p className="line-clamp-1 text-zinc-600 text-base font-semibold mb-1">
                {event.location || 'Online'}
              </p>
              <p className="line-clamp-1 text-zinc-600 text-base font-normal uppercase mb-3">
                {dateInfo.timeRange}
              </p>

              {/* Price and Interested */}
              <div className="flex items-center gap-3 text-zinc-800 text-sm font-normal">
                <div className="flex items-center gap-1.5">
                  <Ticket size={18} className="-rotate-45" />
                  <span>{event?.next_schedule?.price || event?.price || 'Free'}</span>
                </div>
                
                {event?.next_schedule?.interested || event?.interested && (
                  <>
                    <div className="w-1 h-1 bg-zinc-800 rounded-full"></div>
                    <div className="flex items-center gap-1.5">
                      <Heart size={18} />
                      <span>{event?.next_schedule?.interested || event?.interested || 0} interested</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Register Button */}
          <div className="relative w-full inline-flex justify-center items-center">
            <Link to={`/online-coaching/register/${sessionId}`} className="bg-orange-600 rounded-md gap-2.5 py-1 px-9 mt-4 text-center text-white text-base font-medium leading-6 hover:bg-orange-700 transition-colors">
              Register Now
            </Link>
            {event?.next_schedule?.interested || event?.interested && (
              <div className="absolute bottom-0 right-0 bg-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer">
                <Heart size={24} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const userRole = userData?.user?.role;
  const roleId = userRole?.id;
  const isPlus = userData?.isPlus || false;
  let planUntil = userData?.planValidUntil;
  const planEnded = planUntil ? (new Date(planUntil) < new Date()) : true;
  let duration = 'none';
  let include = '0';

  if (planUntil && roleId > 1 && roleId < 5) {
    duration = isPlus ? '3 months' : '1 month';
    if (planEnded) {
      duration = 'ended';
    } else if (isPlus) {
      include = '6';
    } else {
      include = '1';
    }
  } else {
    planUntil = null;
  }

  return (
    <EventWrapper
      title="Online Coaching"
      description="Advance your career through online coaching with Skillsture Plus. Gain clarity, skills, and confidence to move forward in your journey."
      planDuration={duration}
      planIncludes={`${include}x sessions`}
      loading={loading}
      error={error}
      eventsCount={events?.length}
      seeMoreLink="/online-coaching"
      seeMoreText="See More"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
        {events.slice(0, 3).map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </EventWrapper>
  );
};

export default OnlineCoaching;